import { useContext } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { CTAButton } from '@bbkPricingPage/components/card/components/cta-button';
import { PricingTableContext } from '@bbkPricingPage/context/pricing-table-context';
import {
  calculateTotalPrice,
  getApplicableAddonsOrCharges,
} from '@bbkPricingPage/helpers/addon-helper';
import { getEffectiveAmount } from '@bbkPricingPage/helpers/amount-calculator';
import { processAddons } from '@bbkPricingPage/helpers/order-summary-helper';
import {
  PricingTableHelper,
  resolvePlanQuantity,
  resolveQuantityMeta,
  type ActiveItemPrice,
} from '@bbkPricingPage/helpers/pricing-table-helper';
import { getFormattedAmount } from '@bbkPricingPage/lib/currencies';
import { findMatchingFrequency } from '@bbkPricingPage/lib/price-utils';
import { getSubscribedAddonQuantities } from '@bbkPricingPage/lib/subscription-utils';
import {
  AddonType,
  ItemType,
  PricingModel,
  SubscriptionStatus,
  type Discount,
  type Subscription,
} from '@bbkPricingPage/types/pricing-table-types';
import { PriceSplit } from './price-split';

export const DISABLED_SUBSCRIPTION_STATUSES = [
  SubscriptionStatus.future,
  SubscriptionStatus.non_renewing,
  SubscriptionStatus.transferred,
  SubscriptionStatus.paused,
] as const;

export const OrderSummary = ({
  selectedPlanPrice,
  selectedAddons,
  subscription,
  applicableDiscounts,
}: {
  selectedPlanPrice: ActiveItemPrice;
  selectedAddons: Map<string, { addonId: string; quantity: number }>;
  subscription?: Subscription;
  applicableDiscounts?: Discount[];
}) => {
  const { item, price, isSubscribedPrice } = selectedPlanPrice;
  const {
    pricingTable,
    currency,
    priceVariantId,
    priceQuantities,
    quantity: initQuantity,
    handlePrimaryCtaClick,
    isCheckoutLoading,
  } = useContext(PricingTableContext);
  const frequencies = pricingTable?.frequencies ?? [];
  const quantityMeta = resolveQuantityMeta(item, price);
  const subscriptionPlan = PricingTableHelper.getSubscribedPlan(subscription);

  const planQuantity = resolvePlanQuantity({
    priceId: price.billing_system_id,
    priceQuantities,
    initQuantity,
    isSubscribedPrice,
    subscribedPlanQuantity: subscriptionPlan?.quantity,
    quantityMeta,
  });
  const addons = pricingTable?.addons ?? [];

  const frequency = findMatchingFrequency(price, frequencies);
  const processedAddons = processAddons(
    selectedAddons,
    currency.currency_code,
    frequency,
    item,
    price,
    addons,
    priceVariantId
  );
  if (!processedAddons) return null;

  // ==============================================================
  let changedAddons: typeof processedAddons.processedAddons = [];
  let newAddons: typeof processedAddons.processedAddons = [];
  let unchangedAddons: typeof processedAddons.processedAddons = [];

  if (subscription?.subscription_items) {
    const subscribedAddons = new Map<
      string,
      { addonId: string; quantity: number | undefined }
    >();
    if (subscription?.subscription_items) {
      const subscribedAddonQuantities = getSubscribedAddonQuantities(
        subscription,
        addons
      );
      subscribedAddonQuantities.forEach((saq) => {
        subscribedAddons.set(saq.addonId, {
          addonId: saq.addonId,
          quantity: saq.quantity,
        });
      });
    }

    const processedSubscribedAddons = processAddons(
      subscribedAddons,
      currency.currency_code,
      frequency,
      item,
      price,
      addons,
      priceVariantId
    );
    if (
      processedSubscribedAddons &&
      subscription?.subscription_items.length > 0
    ) {
      unchangedAddons = processedAddons.processedAddons.filter((pAddon) => {
        if (!pAddon) return;
        const matchAddon = processedSubscribedAddons.processedAddons.find(
          (psa) =>
            pAddon.addon.billing_system_id === psa?.addon.billing_system_id &&
            pAddon.quantity === psa.quantity
        );
        return !!matchAddon;
      });

      changedAddons = processedSubscribedAddons.processedAddons.filter(
        (psa) => {
          const matchAddon = processedAddons.processedAddons.find(
            (pa) => pa?.addon.billing_system_id === psa?.addon.billing_system_id
          );
          return !matchAddon || matchAddon.quantity !== psa?.quantity;
        }
      );

      newAddons = processedAddons.processedAddons
        .filter((pa) => {
          const matchAddon = processedSubscribedAddons.processedAddons.find(
            (psa) =>
              psa?.addon.billing_system_id === pa?.addon.billing_system_id
          );
          return !matchAddon || matchAddon.quantity !== pa?.quantity;
        })
        .filter((pa) => {
          return !unchangedAddons.some(
            (ua) => ua?.addon.billing_system_id === pa?.addon.billing_system_id
          );
        });
    }
  }

  const addonsDataForCheckout = processedAddons.processedAddons
    .filter((item) => item !== null && item != undefined)
    .filter((Item) => Item.addonType === AddonType.recommended)
    .map((item, index) => {
      const data = {
        item: item.addon,
        price: item.addonPrice,
        quantity:
          item.addonPrice.pricing_model !== PricingModel.flat_fee
            ? (item.quantity ?? 1)
            : null,
        index: index + 1,
      };
      return data;
    });

  // ==============================================================

  const planPriceValue = getEffectiveAmount(
    price,
    planQuantity,
    frequency,
    applicableDiscounts
  );

  const hasChanges =
    (changedAddons?.length ?? 0) > 0 || (newAddons?.length ?? 0) > 0;

  let headingText = 'Order Summary';
  if (subscription) headingText = 'Updated Subscription';
  if (isSubscribedPrice) {
    headingText = hasChanges ? 'Updated Subscription' : 'Current Subscription';
  }

  const applicableCharges = getApplicableAddonsOrCharges({
    allAddons: addons,
    selectedPlanPrice,
    priceVariant: priceVariantId,
    subscription,
    isSubscribedPrice: isSubscribedPrice,
    itemType: ItemType.charge,
    frequency,
  });

  const chargesTotal = calculateTotalPrice({
    applicableAddons: applicableCharges,
    frequency,
    itemType: ItemType.charge,
  });

  // Extract Record<addonId, quantity> - split into recommended and mandatory
  const recommendedAddonsMap: Record<string, number> = {};
  const mandatoryAddonsMap: Record<string, number> = {};

  processedAddons.processedAddons.forEach((item) => {
    if (item) {
      if (item.addonType === AddonType.recommended) {
        recommendedAddonsMap[item.addon.billing_system_id] = item.quantity ?? 1;
      } else {
        mandatoryAddonsMap[item.addon.billing_system_id] = item.quantity ?? 1;
      }
    }
  });

  applicableCharges.forEach((item) => {
    const attachedAddon = selectedPlanPrice.item.attached_addons?.find(
      (aa) => aa.billing_system_id === item.addon.billing_system_id
    );

    if (attachedAddon?.type === AddonType.recommended) {
      recommendedAddonsMap[item.addon.billing_system_id] = item.quantity ?? 1;
    } else {
      mandatoryAddonsMap[item.addon.billing_system_id] = item.quantity ?? 1;
    }
  });

  return (
    <div className="!tw-bg-[var(--pricing-page-card-background-color)] tw-p-8 @lg:tw-w-1/3">
      <p className="tw-mb-4 !tw-text-xl tw-font-semibold tw-text-[var(--pricing-page-text-font-text-color)]">
        {headingText}
      </p>

      <div className="tw-space-y-3">
        <div className="tw-flex tw-justify-between">
          <div>
            <span className="tw-text-[var(--pricing-page-text-font-text-color)]">
              {item.customization.display_name ?? item.customization.name}
              <PriceSplit
                price={selectedPlanPrice.price}
                quantity={planQuantity}
                selectedPlanId={item.billing_system_id}
                frequency={frequency}
              />
            </span>
          </div>
          <span className="tw-font-medium tw-text-[var(--pricing-page-text-font-text-color)]">
            {getFormattedAmount(planPriceValue, price.currency)}
          </span>
        </div>

        {/* 1. When not in subscribed plan price */}
        {!isSubscribedPrice &&
          processedAddons.processedAddons.map(
            ({ addon, addonPrice, quantity, multiplier, amount }) => (
              <div
                key={`unchanged-${addon.billing_system_id}`}
                className="tw-flex tw-justify-between"
              >
                <div>
                  <span className="tw-text-[var(--pricing-page-text-font-text-color)]">
                    {addon.customization?.name}
                  </span>
                  <PriceSplit
                    price={addonPrice}
                    quantity={quantity}
                    selectedPlanId={item.billing_system_id}
                    frequency={frequency}
                    multiplier={multiplier}
                  />
                </div>
                <span className="tw-font-medium tw-text-[var(--pricing-page-text-font-text-color)]">
                  {getFormattedAmount(amount, price.currency)}
                </span>
              </div>
            )
          )}

        {/* 2. UNCHANGED SUBSCRIBED ADDONS */}
        {isSubscribedPrice &&
          unchangedAddons.map(
            ({ addon, addonPrice, quantity, amount, multiplier }) => (
              <div
                key={`unchanged-${addon.billing_system_id}`}
                className="tw-flex tw-justify-between"
              >
                <div>
                  <span className="tw-text-[var(--pricing-page-text-font-text-color)]">
                    {addon.customization?.name}
                  </span>
                  <PriceSplit
                    price={addonPrice}
                    quantity={quantity}
                    selectedPlanId={item.billing_system_id}
                    frequency={frequency}
                    multiplier={multiplier}
                  />
                </div>
                <span className="tw-font-medium tw-text-[var(--pricing-page-text-font-text-color)]">
                  {getFormattedAmount(amount, price.currency)}
                </span>
              </div>
            )
          )}

        {/* 3. CHANGED SUBSCRIBED ADDONS (WITH STRIKETHROUGH) */}
        {isSubscribedPrice &&
          changedAddons.length > 0 &&
          changedAddons.map(
            ({ addon, addonPrice, quantity, amount, multiplier }) => (
              <div
                key={`changed-${addon.billing_system_id}`}
                className="tw-flex tw-justify-between"
              >
                <div>
                  <span className="tw-text-[var(--pricing-page-text-font-text-color)] tw-line-through">
                    {addon.customization?.name}
                  </span>
                  <PriceSplit
                    price={addonPrice}
                    quantity={quantity}
                    selectedPlanId={item.billing_system_id}
                    frequency={frequency}
                    multiplier={multiplier}
                    strikethrough={true}
                  />
                </div>
                <span className="tw-font-medium tw-text-[var(--pricing-page-text-font-text-color)] tw-line-through">
                  {getFormattedAmount(amount, price.currency)}
                </span>
              </div>
            )
          )}

        {applicableCharges &&
          applicableCharges.length > 0 &&
          applicableCharges.map(({ addon, price, quantity }) => {
            const chargePriceData = getEffectiveAmount(
              price,
              quantity,
              frequency
            );
            return (
              <div
                key={`changed-${addon.billing_system_id}`}
                className="tw-flex tw-justify-between"
              >
                <div>
                  <span className="tw-text-[var(--pricing-page-text-font-text-color)]">
                    {addon.customization?.name}
                  </span>
                  <PriceSplit
                    price={price}
                    quantity={quantity}
                    selectedPlanId={item.billing_system_id}
                    frequency={frequency}
                  />
                </div>
                <span className="tw-font-medium tw-text-[var(--pricing-page-text-font-text-color)]">
                  {getFormattedAmount(chargePriceData, price.currency)}
                </span>
              </div>
            );
          })}

        {selectedPlanPrice.isSubscribedPrice && newAddons.length > 0 && (
          <>
            <div className="tw-border-t tw-border-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_20%,transparent)] tw-pt-3">
              <div className="!tw-text-sm tw-font-medium tw-text-[var(--pricing-page-text-font-text-color)]">
                What&apos;s new
              </div>
              <div className="tw-space-y-2">
                {newAddons.map(
                  ({ addon, addonPrice, quantity, amount, multiplier }) => (
                    <div
                      key={`new-${addon.billing_system_id}`}
                      className="tw-flex tw-justify-between"
                    >
                      <div>
                        <span className="tw-text-[var(--pricing-page-text-font-text-color)]">
                          {addon.customization?.name}
                        </span>
                        <PriceSplit
                          price={addonPrice}
                          quantity={quantity}
                          selectedPlanId={item.billing_system_id}
                          frequency={frequency}
                          multiplier={multiplier}
                        />
                      </div>
                      <span className="tw-font-medium tw-text-[var(--pricing-page-text-font-text-color)]">
                        {getFormattedAmount(amount, price.currency)}
                      </span>
                    </div>
                  )
                )}
              </div>
            </div>
          </>
        )}

        <div className="tw-border-t tw-border-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_20%,transparent)] tw-pt-3">
          <div className="tw-flex tw-justify-between">
            <span className="tw-font-semibold tw-text-[var(--pricing-page-text-font-text-color)]">
              Total
            </span>
            <span className="tw-font-semibold tw-text-[var(--pricing-page-text-font-text-color)]">
              {getFormattedAmount(
                processedAddons.total + planPriceValue + chargesTotal,
                price.currency
              )}
            </span>
          </div>
        </div>

        {hasChanges &&
          subscription &&
          !DISABLED_SUBSCRIPTION_STATUSES.includes(
            subscription.status ?? ''
          ) && (
            <div className="tw-rounded-pricify-border-radius tw-border-pricify-brand tw-bg-pricify-brand/5 tw-text-pricify-brand tw-border tw-p-2">
              <div className="tw-flex tw-items-start">
                <InformationCircleIcon className="tw-text-pricify-brand tw-mr-2 tw-flex tw-h-6 tw-w-6 tw-self-start !tw-text-xs" />
                <span className="!tw-text-sm">
                  Payable amount will be shown in checkout.
                </span>
              </div>
            </div>
          )}
        {DISABLED_SUBSCRIPTION_STATUSES.includes(
          subscription?.status ?? ''
        ) && (
          <div className="tw-rounded-pricify-border-radius tw-border-pricify-brand tw-bg-pricify-brand/5 tw-text-pricify-brand tw-border tw-p-2">
            <div className="tw-flex tw-items-start">
              <InformationCircleIcon className="tw-text-pricify-brand tw-mr-2 tw-flex tw-h-12 tw-w-12 tw-self-start !tw-text-xs" />
              <span className="!tw-text-sm">
                You&apos;re unable to create a new subscription right now.
                Please reach out to support for assistance.
              </span>
            </div>
          </div>
        )}

        <CTAButton
          itemId={item.billing_system_id}
          price={price}
          actionButton={item.customization.action_button}
          paywallConfig={pricingTable?.paywall_config}
          isSubscribedPrice={isSubscribedPrice}
          quantity={planQuantity}
          quantityMeta={quantityMeta}
          subscriptionQuantity={
            subscription?.subscription_items?.find(
              (si) =>
                si.billing_system_item_price_id === price.billing_system_id
            )?.quantity
          }
          hasChanges={hasChanges}
          subscriptionStatus={subscription?.status as string}
          disabled={isCheckoutLoading}
          onClick={() =>
            handlePrimaryCtaClick({
              item: item,
              price: price,
              quantity: planQuantity,
              subscription: subscription,
              isSubscribedPrice: isSubscribedPrice,
              paywallConfig: pricingTable?.paywall_config,
              addonsDataForCheckout: addonsDataForCheckout,
              recommendedAddonsMap: recommendedAddonsMap,
              mandatoryAddonsMap: mandatoryAddonsMap,
            })
          }
        />
      </div>
    </div>
  );
};
